import Section, { SectionVisibilityProps } from './Section';
import { useTranslation } from 'react-i18next';
import TextInput from '../../inputs/TextInput';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import CheckmarkTextInput from '../../inputs/CheckmarkTextInput';
import collect from 'collect.js';
import { SectionType } from './CheckoutInfoBag';
import { Checkbox } from '../../ui/checkbox';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useStore from '../../../hooks/store/useStore';
import { REDUX_STORE_SLICES } from '../../../services/exports/Constants';

const EMAIL_REGEX: RegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export default function InvoiceInfoSection({
  show,
  validationErrors: generalValidationErrors,
}: SectionVisibilityProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:InvoiceInfoSection' });

  const { localData, update, validationErrors: orderValidationsError } = useOrderFlow();
  const { flattenValidationErrors } = useAppMethods();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const validationErrors = generalValidationErrors ?? orderValidationsError;

  const isFieldValid = (value: string, extraChecks?: ((value: string) => boolean)[]) =>
    value?.length > 0 && collect(extraChecks).every((callback) => callback(value));

  // Gated on the provider's issue_end_customer_receipts flag: providers that do not offer
  // end-customer invoices never show the request, and the backend drops any invoice fields
  // posted for their orders anyway. Rendering nothing rather than a collapsed Section keeps
  // checkout flowing straight from the order summary to payment, with no leftover gap.
  if (!company?.service_provider?.issues_end_customer_receipts) {
    return null;
  }

  return (
    <Section show={show} id={SectionType.invoice_info}>
      <div className="w-full cursor-pointer">
        <div className="flex w-full items-start gap-xs">
          <Checkbox
            checked={localData.is_receipt_required ?? false}
            onCheckedChange={(checked) => update('is_receipt_required', checked)}
            id="is-receipt-required"
            className="my-auto"
          />
          <div className="cursor-pointer w-full">
            <label
              htmlFor="is-receipt-required"
              className="my-auto flex flex-col gap-1 cursor-pointer text-sm text-muted-foreground"
            >
              {t('title')}
            </label>
          </div>
        </div>
        {localData.is_receipt_required && (
          <div className="mt-md grid grid-cols-2 gap-2">
            <TextInput
              value={localData.invoice_company_name}
              onChange={(value) => update('invoice_company_name', value)}
              placeholder={t('form.company_name.placeholder')}
              inputContainerStyle="col-span-2"
            />
            <TextInput
              value={localData.invoice_first_name}
              onChange={(value) => update('invoice_first_name', value)}
              placeholder={t('form.first_name.placeholder')}
              inputContainerStyle="lg:col-span-1 col-span-2"
            />
            <TextInput
              value={localData.invoice_last_name}
              onChange={(value) => update('invoice_last_name', value)}
              placeholder={t('form.last_name.placeholder')}
              inputContainerStyle="lg:col-span-1 col-span-2"
            />
            <TextInput
              value={localData.invoice_address}
              onChange={(value) => update('invoice_address', value)}
              placeholder={t('form.address.placeholder')}
              inputContainerStyle="col-span-2"
            />
            <TextInput
              value={localData.invoice_zip_code}
              onChange={(value) => update('invoice_zip_code', value)}
              placeholder={t('form.zip_code.placeholder')}
            />
            <TextInput
              value={localData.invoice_city}
              onChange={(value) => update('invoice_city', value)}
              placeholder={t('form.city.placeholder')}
            />
            <TextInput
              value={localData.invoice_tax_id}
              onChange={(value) => update('invoice_tax_id', value)}
              placeholder={t('form.tax_id.placeholder')}
              inputContainerStyle="col-span-2"
            />
            <CheckmarkTextInput
              value={localData.invoice_email}
              isComplete={isFieldValid(localData.invoice_email, [
                (value: string) => EMAIL_REGEX.test(value.toLocaleLowerCase()),
              ])}
              onChange={(value) => update('invoice_email', value)}
              placeholder={t('form.email.placeholder')}
              classes={{
                container: 'col-span-2',
              }}
              validationErrors={flattenValidationErrors('invoice_email', validationErrors, t)}
            />
          </div>
        )}
      </div>
    </Section>
  );
}
